import { useRef, type KeyboardEvent, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface SegmentOption<T extends string> {
  value: T
  label: ReactNode
  /** The count pill (`.ct`). */
  count?: number
}

interface SegmentedProps<T extends string> {
  /** Names the group for screen readers, e.g. "Filter agents by status". */
  label: string
  options: SegmentOption<T>[]
  value: T
  onChange: (value: T) => void
  className?: string
  /** Per-option attributes, e.g. the prototype's `data-cfilter="attention"` Ember variant. */
  optionProps?: (value: T) => Record<string, string>
}

/** A segmented filter (`.seg`): toggle buttons with `aria-pressed`, one pressed at a time. */
export function Segmented<T extends string>({ label, options, value, onChange, className, optionProps }: SegmentedProps<T>) {
  return (
    <div className={cn('seg', className)} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
          {...optionProps?.(option.value)}
        >
          {option.label}
          {option.count !== undefined ? <span className="ct">{option.count}</span> : null}
        </button>
      ))}
    </div>
  )
}

interface TabsProps<T extends string> {
  label: string
  tabs: { value: T; label: ReactNode }[]
  value: T
  onChange: (value: T) => void
  /** Prefix for the tab and panel ids; panels use `${idBase}-panel-${value}`. */
  idBase: string
  className?: string
}

/**
 * Tabs (`.tabs`, `role="tablist"`). Arrow keys move between tabs. Render the active
 * panel yourself with `role="tabpanel"` and `aria-labelledby={`${idBase}-tab-${value}`}`.
 */
export function Tabs<T extends string>({ label, tabs, value, onChange, idBase, className }: TabsProps<T>) {
  const list = useRef<HTMLDivElement>(null)
  const onKeyDown = (event: KeyboardEvent) => {
    const index = tabs.findIndex((tab) => tab.value === value)
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0
    if (!step) return
    event.preventDefault()
    const next = tabs[(index + step + tabs.length) % tabs.length]
    onChange(next.value)
    list.current?.querySelector<HTMLButtonElement>(`#${CSS.escape(`${idBase}-tab-${next.value}`)}`)?.focus()
  }
  return (
    <div ref={list} className={cn('tabs', className)} role="tablist" aria-label={label} onKeyDown={onKeyDown}>
      {tabs.map((tab) => (
        <button
          key={tab.value}
          id={`${idBase}-tab-${tab.value}`}
          type="button"
          role="tab"
          aria-selected={tab.value === value}
          aria-controls={`${idBase}-panel-${tab.value}`}
          tabIndex={tab.value === value ? 0 : -1}
          onClick={() => onChange(tab.value)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}
